<template>
    <!-- Header -->
    <div class="flex justify-between items-center pb-3">
        <h4 class="font-bold mb-0">{{ t('customFields.title') }}</h4>
        <Button :icon="viewMode === 'table' ? 'fa-solid fa-grip' : 'fa-solid fa-table'" @click="toggleView" outlined />
    </div>

    <div class="pb-4">
        <!-- Card View -->
        <CustomFieldCardView v-if="viewMode === 'card'" :custom-fields="customFields" :loading="loading"
            :search="cardSearch" :status-filter="cardStatusFilter" :first="cardFirst" :rows="cardPageSize"
            :total-records="cardMetadata?.filtered || 0" @search="onCardSearch" @status-change="onCardStatusChange"
            @page="onCardPage" @create="customFieldModal.open('create')" @edit="customFieldModal.open('edit', $event)"
            @delete="customFieldModal.open('delete', $event)" />

        <!-- Table View -->
        <CustomFieldTableView v-else :custom-fields="customFields" :loading="loading" :filters="tableFilters"
            :sort-field="tableSortField" :sort-order="tableSortOrder" :first="tableFirst" :rows="tablePageSize"
            :total-records="tableMetadata?.filtered || 0" @page="onTablePage" @filter="onTableFilter"
            @sort="onTableSort" @clear-filters="clearTableFilters" @create="customFieldModal.open('create')"
            @edit="customFieldModal.open('edit', $event)" @delete="customFieldModal.open('delete', $event)" />

        <!-- Modal -->
        <CustomFieldModal v-if="customFieldModal.isOpen.value" :visible="customFieldModal.isOpen.value"
            :mode="customFieldModal.mode.value" :customField="customFieldModal.data.value"
            @close="customFieldModal.close" @saved="onCustomFieldSaved" />
    </div>
</template>

<script setup lang="ts">
import CustomFieldCardView from '@/components/customFields/CustomFieldCardView.vue';
import CustomFieldTableView from '@/components/customFields/CustomFieldTableView.vue';
import CustomFieldModal from '@/components/modal/CustomField.vue';
import { useCustomFieldsPage } from '@/composables/useCustomFieldsPage';
import { useModal } from '@/composables/useModal';
import type { CustomField } from '@/lib/types/customField';
import { getInitialViewMode, ViewModeKey } from '@/utils/datatable';
import { useToast } from 'primevue/usetoast';
import { onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const toast = useToast();

// Modal
const customFieldModal = useModal<CustomField>();

// Page logic
const {
    // State
    viewMode,
    loading,
    customFields,

    // Table
    tableFilters,
    tableSortField,
    tableSortOrder,
    tableFirst,
    tablePageSize,
    tableMetadata,

    // Card
    cardSearch,
    cardStatusFilter,
    cardFirst,
    cardPageSize,
    cardMetadata,

    // Methods
    toggleView,
    fetchData,
    initialize,
    onTablePage,
    onTableFilter,
    onTableSort,
    clearTableFilters,
    onCardPage,
    filterCards
} = useCustomFieldsPage(getInitialViewMode(ViewModeKey.CUSTOM_FIELDS));

// Card view handlers with debouncing
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

function onCardSearch(value: string) {
    cardSearch.value = value;

    if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
    }

    searchDebounceTimer = setTimeout(() => {
        filterCards();
    }, 500);
}

function onCardStatusChange(value: boolean | null) {
    cardStatusFilter.value = value;
    filterCards();
}

// Event handlers
function onCustomFieldSaved() {
    fetchData();
    toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: t('customFields.saveSuccess'),
        life: 3000
    });
}

// Lifecycle
onMounted(async () => {
    await initialize();
});
</script>
